import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

/**
 * Przełącznik „Moje / Zespół” (menu boczne, tylko Admin i Manager). Moduły czytają `useScope()`
 * i filtrują listy (Zespół = dane całego workspace'u w zakresie uprawnień). Pamiętany w przeglądarce.
 */
export type Scope = 'mine' | 'team';

const STORAGE_KEY = 'fos.scope';

interface ScopeContextValue {
  scope: Scope;
  setScope: (scope: Scope) => void;
}

const ScopeContext = createContext<ScopeContextValue>({ scope: 'mine', setScope: () => {} });

function readStored(): Scope {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'team' ? 'team' : 'mine';
  } catch {
    return 'mine';
  }
}

export function ScopeProvider({ children, enabled }: { children: ReactNode; enabled: boolean }) {
  const [scope, setScope] = useState<Scope>(() => (enabled ? readStored() : 'mine'));

  useEffect(() => {
    if (!enabled) setScope('mine');
  }, [enabled]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, scope);
    } catch {
      // tryb prywatny: bez zapamiętywania
    }
  }, [scope]);

  return (
    <ScopeContext.Provider value={{ scope: enabled ? scope : 'mine', setScope }}>{children}</ScopeContext.Provider>
  );
}

export function useScope(): ScopeContextValue {
  return useContext(ScopeContext);
}
